import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchProjectFinancialSummaryPaged,
  getDataMode,
} from '@/lib/data-service';
import type {
  ProjectFinancialSummaryRow,
  ProjectFinancialSummaryPagedParams,
} from '@/lib/data-service';
import type { WIPReport } from '@/types';

export interface FinancialSummaryFilters {
  health: string;
  projectType: string;
  signal: string;
}

export interface FinancialSummaryRowEnriched {
  id: number;
  name: string;
  clientNames: string;
  pmName: string;
  projectType: string;
  recognitionMethod: string | null;
  budgetRevenue: number;
  actualSales: number;
  grossMarginPct: number | null;
  wipAmount: number;
  completionPercent: number;
  health: string;
  signals: { label: string; colour: string }[];
}

const PAGE_SIZE = 15;

export function usePagedFinancialSummary(
  wipReports: WIPReport[],
  revenueRecognitions: { projectId: number; revenueToRecognise?: number; method?: string; budgetRevenue?: number }[],
  isLiveMode: boolean,
  companyId: string | number | undefined,
) {
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<FinancialSummaryFilters>({
    health: 'all',
    projectType: 'all',
    signal: 'all',
  });
  const [rows, setRows] = useState<ProjectFinancialSummaryRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  const fetchPage = useCallback(async (p: number, f: FinancialSummaryFilters) => {
    const mode = getDataMode();
    if (mode !== 'live') return;

    setLoading(true);
    const params: ProjectFinancialSummaryPagedParams = {
      page: p,
      pageSize: PAGE_SIZE,
      sortColumn: 'WipAmount',
      sortDirection: 'DESC',
      health: f.health !== 'all' ? f.health : null,
      projectType: f.projectType !== 'all' ? f.projectType : null,
    };

    const response = await fetchProjectFinancialSummaryPaged(params);
    if (isMountedRef.current) {
      setRows(response.rows);
      setTotalCount(response.totalCount);
      setLoading(false);
    }
  }, []);

  // Fetch when page, filters, or company changes
  useEffect(() => {
    if (!isLiveMode || !companyId) return;
    void fetchPage(page, filters);
  }, [page, filters, companyId, isLiveMode, fetchPage]);

  // Enrich rows with signals (computed from wipReports/revRec joins)
  const enrichedRows: FinancialSummaryRowEnriched[] = rows.map(p => {
    const wip = wipReports.find(w => w.projectId === p.id);
    const rev = revenueRecognitions.find(r => r.projectId === p.id);
    const actualSales = p.actualSales ?? 0;
    const wipAmount = wip?.wipAmount ?? p.wipAmount ?? 0;
    const agingDays = wip?.agingDays ?? 0;
    const grossMarginPct = p.grossMarginPercent ?? null;
    const overBilling = wip?.overBilling ?? 0;
    const revToRecPrj = rev?.revenueToRecognise ?? 0;

    const signals: { label: string; colour: string }[] = [];
    if ((p.costVariancePercent ?? 0) < -10) signals.push({ label: 'Rev Under', colour: 'destructive' });
    if (grossMarginPct !== null && grossMarginPct < 20) signals.push({ label: 'Margin Risk', colour: 'warning' });
    if (wipAmount > 10000 && agingDays > 30) signals.push({ label: 'WIP High', colour: 'warning' });
    if (overBilling > 0) signals.push({ label: 'Over-Billed', colour: 'destructive' });
    if (revToRecPrj > 0) signals.push({ label: 'Unrecognised', colour: 'blue' });

    return {
      id: p.id,
      name: p.name,
      clientNames: p.clientNames ?? '',
      pmName: p.pmName ?? wip?.pmName ?? '—',
      projectType: p.projectType ?? wip?.projectType ?? '—',
      recognitionMethod: p.revenueRecognitionMethod ?? rev?.method ?? null,
      budgetRevenue: p.budgetRevenue ?? rev?.budgetRevenue ?? 0,
      actualSales,
      grossMarginPct,
      wipAmount,
      completionPercent: p.completionPercent ?? 0,
      health: p.health ?? 'unknown',
      signals: signals.slice(0, 2),
    };
  });

  // Client-side signal filter (signals are computed from joined data, can't be in SQL)
  const filteredRows = filters.signal !== 'all'
    ? enrichedRows.filter(r => r.signals.some(s => s.label.toLowerCase().includes(filters.signal.toLowerCase())))
    : enrichedRows;

  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  const updateFilters = useCallback((newFilters: Partial<FinancialSummaryFilters>) => {
    setFilters(f => ({ ...f, ...newFilters }));
    setPage(0);
  }, []);

  const refresh = useCallback(() => {
    void fetchPage(page, filters);
  }, [page, filters, fetchPage]);

  return {
    rows: filteredRows,
    totalCount,
    page,
    setPage,
    pageSize: PAGE_SIZE,
    pageCount,
    filters,
    updateFilters,
    loading,
    refresh,
  };
}
